import debounce from 'lodash/debounce'
import { getAllScopeTargets } from './textanimations'
import {
	applyPreRevealVisualStateToTarget,
	taResizeTrace,
	taResizeTraceTarget,
	taSplitLog,
	taSplitLogTarget,
} from './text_animation_resplit_recovery'
import { ensureLayPretextTextAnimationLoaded, someVisibleTargetNeedsPretextLazyModule } from './pretext_lazy'
import { applyLineMaskPaddingBottomStyle } from './line_mask_padding_bottom'
import {
	consolidateTypewriterParagraphIntoSingleLine,
	ensureWhitespaceBetweenAdjacentWordElements,
	getLineElementsForWrap,
	getTextAnimationBlockElements,
	normalizeWhitespaceOnlyTextNodes,
} from './split_line_wrap'
import {
	waitForTextAnimationFonts,
	whenTextAnimationFontsAreReady,
} from './text_animation_font_readiness'

const PENDING_RESPLIT_ATTR = 'data-lay-ta-pending-resplit'
const PRETEXT_WIDTH_INSET_ATTR = 'data-lay-pretext-width-inset'
let lateFontResplitScheduled = false

const NO_LINE_MASK_ANIMATION_TYPES = new Set([
	'fade_characters',
	'typewriter_lines',
	'typewriter_words',
	'typewriter',
	'typewriter_fade',
	'fade_and_slide_lines',
	'fade_and_slide_words',
	'fade_and_slide_characters',
	'fade_and_slide_whole_text',
])

function shouldUseLineMaskForAnimationType(animationType) {
	return !NO_LINE_MASK_ANIMATION_TYPES.has(animationType)
}

function waitForTextAnimationFontsIfNeeded(targets) {
	if (!someVisibleTargetNeedsPretextLazyModule(targets)) {
		return Promise.resolve(true)
	}
	return waitForTextAnimationFonts()
}

function scheduleResplitAfterLateFonts() {
	if (lateFontResplitScheduled) {
		return
	}
	lateFontResplitScheduled = true
	whenTextAnimationFontsAreReady()
		.then(() => {
			lateFontResplitScheduled = false
			markDirtyTextAnimationsPendingResplit({
				reason: 'fonts_ready_after_timeout',
				skipIfWidthUnchanged: false,
			})
			return prepareTexts()
		})
		.catch(() => {
			lateFontResplitScheduled = false
		})
}

/**
 * Mark visible dirty text-animation roots for the next prepareTexts resplit.
 * @param {{ reason?: string, skipIfWidthUnchanged?: boolean }} options
 */
function markDirtyTextAnimationsPendingResplit(options = {}) {
	const { reason = 'unknown', skipIfWidthUnchanged = true } = options
	const w = window.innerWidth
	if (skipIfWidthUnchanged && w === oldWindowWidth) {
		if (reason === 'window_width_changed') {
			taResizeTrace('markPendingResplit skipped (innerWidth unchanged)', { innerWidth: w, reason })
		}
		return
	}
	// Resize legitimately needs the union — any visible target on the page
	// might need a re-split when the window width crosses a breakpoint.
	const targets = getAllScopeTargets()
	let marked = 0
	let skipNotVisible = 0
	let skipNotDirty = 0
	let skipNoOriginal = 0
	targets.forEach((target) => {
		if (!jQuery(target).is(':visible')) {
			skipNotVisible++
			return
		}
		if (jQuery(target).data('text-state') !== 'dirty') {
			skipNotDirty++
			return
		}
		if (!jQuery(target).data('original-html')) {
			skipNoOriginal++
			return
		}
		target.setAttribute(PENDING_RESPLIT_ATTR, '1')
		marked++
	})
	taResizeTrace('markPendingResplit', {
		reason,
		fromWidth: oldWindowWidth,
		toWidth: w,
		targetCount: targets.length,
		marked,
		skipNotVisible,
		skipNotDirty,
		skipNoOriginal,
	})
	oldWindowWidth = w
}

const markTextAnimationsPendingResplit = () => {
	markDirtyTextAnimationsPendingResplit({ reason: 'window_width_changed', skipIfWidthUnchanged: true })
}

/** Lay desktop / tablet / phone bucket changed (emitted only when Utility.screenSize actually changes). */
const onLayTextAnimSizeChanged = (newSize) => {
	taResizeTrace('sizechanged → mark pending resplit (grid)', { newSize })
	markDirtyTextAnimationsPendingResplit({ reason: 'sizechanged', skipIfWidthUnchanged: false })
	debouncedResizeEnd()
}

function copyLayTextAnimationAttrsToGhost(target, ghost) {
	ghost.className = target.className
	const names = [
		'data-animation-type',
		'data-animation-easing',
		'data-stagger-easing',
		'data-top',
		'data-duration',
		'data-stagger',
		'data-delay',
		'data-line-mask-padding-bottom',
		PRETEXT_WIDTH_INSET_ATTR,
		'lang',
	]
	names.forEach((name) => {
		const v = target.getAttribute(name)
		if (v != null && v !== '') {
			ghost.setAttribute(name, v)
		}
	})
}

function copyTextMeasurementStylesFromTarget(target, ghost) {
	const cs = window.getComputedStyle(target)
	ghost.style.boxSizing = cs.boxSizing || 'border-box'
	ghost.style.width = `${Math.max(1, target.clientWidth)}px`
	ghost.style.padding = cs.padding
	ghost.style.font = cs.font
	ghost.style.letterSpacing = cs.letterSpacing
	ghost.style.wordSpacing = cs.wordSpacing
	ghost.style.textTransform = cs.textTransform
	ghost.style.lineHeight = cs.lineHeight
	ghost.style.textAlign = cs.textAlign
}

/**
 * Rebuild split DOM off-screen (same width/typography as target), then swap into place in one step —
 * avoids flashing unsplit HTML on the visible element (similar to building a fragment before replace).
 */
function atomicResplitLayTextAnimation(target) {
	const originalHTML = jQuery(target).data('original-html')
	if (!originalHTML) {
		return
	}

	const ghost = document.createElement('div')
	copyLayTextAnimationAttrsToGhost(target, ghost)
	copyTextMeasurementStylesFromTarget(target, ghost)
	ghost.style.position = 'fixed'
	ghost.style.left = '-99999px'
	ghost.style.top = '0'
	ghost.style.visibility = 'hidden'
	ghost.style.pointerEvents = 'none'

	ghost.innerHTML = originalHTML
	document.body.appendChild(ghost)

	splitText(ghost)
	wrapLines(ghost)

	target.innerHTML = ''
	target['🍌'] = null
	while (ghost.firstChild) {
		target.appendChild(ghost.firstChild)
	}
	ghost.remove()
	taSplitLogTarget(target, 'atomicResplitLayTextAnimation: swapped DOM → preReveal')
	applyPreRevealVisualStateToTarget(target)
}

function getPretextLayoutOverflowPx(target) {
	const paragraphs = getTextAnimationBlockElements(target)
	let overflowPx = 0
	for (let i = 0; i < paragraphs.length; i++) {
		const paragraph = paragraphs[i]
		if (paragraph.dataset.pretextSplit !== 'true') {
			continue
		}
		const maxWidth = Math.max(paragraph.clientWidth, Math.round(paragraph.getBoundingClientRect().width))
		if (!maxWidth) {
			continue
		}
		const lines = paragraph.querySelectorAll('.lay-text-line')
		for (let j = 0; j < lines.length; j++) {
			const line = lines[j]
			overflowPx = Math.max(overflowPx, line.scrollWidth - maxWidth)
		}
	}
	return Math.max(0, overflowPx)
}

function waitForNextFrame() {
	return new Promise((resolve) => {
		window.requestAnimationFrame(() => resolve())
	})
}

async function stabilizePostLayoutIfNeeded(target) {
	if (!target || !jQuery(target).data('original-html')) {
		return
	}
	for (let attempts = 0; attempts < 2; attempts++) {
		await waitForNextFrame()
		await waitForNextFrame()
		const overflowPx = getPretextLayoutOverflowPx(target)
		taSplitLogTarget(target, 'stabilizePostLayoutIfNeeded', { attempt: attempts + 1, overflowPx })
		if (overflowPx <= 1) {
			return
		}
		const previousInset = parseFloat(target.getAttribute(PRETEXT_WIDTH_INSET_ATTR)) || 0
		target.setAttribute(PRETEXT_WIDTH_INSET_ATTR, String(Math.ceil(previousInset + overflowPx + 1)))
		atomicResplitLayTextAnimation(target)
	}
}

/**
 * Walks a paragraph's text nodes (recursively into element children like
 * `<a class="laybutton">`) and wraps each character in a `<span class="char">`.
 * Used as a defensive fallback when the vanilla `Splitting` library throws
 * on the paragraph's structure (we've seen `n.style is null` deep in the
 * Splitting vendor when the `<p>` contains a `<a class="laybutton">` with
 * many data-* attributes — Splitting fails to handle the anchor reliably).
 *
 * The chars end up flat inside the paragraph (and inside any anchor), and
 * `wrapLines` (called right after `splitText` in `prepareTexts`) wraps the
 * paragraph in a `.lay-text-line` div which is what the timeline targets.
 */
const manuallySplitParagraphIntoChars = (paragraph) => {
	// Splitting may have partially completed before throwing — words are
	// already wrapped, and chars may already exist inside them. Walking blind
	// would re-wrap each char's text in another `.char`, producing visible
	// double-nested spans. Strip any existing split markup back to plain text
	// first so we always start from a clean slate.
	stripSplitMarkupIfPresent(paragraph)
	let charIndex = 0
	const visit = (node) => {
		if (node.nodeType === Node.TEXT_NODE) {
			const text = node.nodeValue
			if (!text) return
			const fragment = document.createDocumentFragment()
			for (const ch of text) {
				const isWhitespace = /\s/.test(ch)
				const span = document.createElement('span')
				span.className = isWhitespace ? 'whitespace' : 'char'
				if (!isWhitespace) span.setAttribute('data-char', ch)
				span.style.setProperty('--char-index', charIndex)
				span.textContent = ch
				fragment.appendChild(span)
				charIndex++
			}
			node.parentNode.replaceChild(fragment, node)
			return
		}
		if (node.nodeType === Node.ELEMENT_NODE) {
			// Snapshot the children before mutating so we don't iterate
			// over the just-inserted spans.
			const children = Array.from(node.childNodes)
			children.forEach(visit)
		}
	}
	visit(paragraph)
	paragraph.classList.add('words')
	paragraph.style.setProperty('--char-total', charIndex)
}

/**
 * Replaces a `.word` span's text content with `.char` spans (one per
 * character). Used after `Splitting({ by: 'lines' })` for `masked_characters`
 * when Pretext bails — splitting words manually keeps the surrounding DOM
 * (e.g. the parent `<a class="laybutton">`) untouched, so the link survives.
 */
const splitWordIntoCharSpans = (word) => {
	const text = word.textContent || ''
	if (!text) return
	word.innerHTML = ''
	let charIndex = 0
	for (const ch of text) {
		const isWhitespace = /\s/.test(ch)
		const span = document.createElement('span')
		span.className = isWhitespace ? 'whitespace' : 'char'
		if (!isWhitespace) span.setAttribute('data-char', ch)
		span.style.setProperty('--char-index', charIndex)
		span.textContent = ch
		word.appendChild(span)
		charIndex++
	}
	word.style.setProperty('--char-total', charIndex)
}

// Function to split text into lines or characters based on animation type
const splitText = (target) => {
	const pt = window.layPretextTextAnimation
	const paragraphs = getTextAnimationBlockElements(target)

	// Check animation type to determine splitting method
	const animationType = target.getAttribute('data-animation-type')
	const splitBy = animationType === 'typewriter' || animationType === 'typewriter_fade' ? 'chars' : 'lines'
	const useLineMaskForType = shouldUseLineMaskForAnimationType(animationType)

	// Split each paragraph individually
	paragraphs.forEach((paragraph) => {
		if (pt && pt.shouldUsePretextForAnimationType(animationType) && pt.splitParagraphWithPretext(paragraph, useLineMaskForType)) {
			return
		}
		if (pt && pt.shouldUsePretextWordLevelAnimation(animationType) && pt.splitParagraphWithPretextThenWords(paragraph, useLineMaskForType)) {
			return
		}
		if (pt && pt.shouldUsePretextCharLevelAnimation(animationType)) {
			// `masked_characters` needs the per-line mask wrapper so each
			// char can slide up out of it; other char-level types (none yet
			// in the set, but typewriter_*-style additions in the future)
			// can opt out by listing themselves elsewhere.
			const useMaskForChars = animationType === 'masked_characters'
			if (pt.splitParagraphWithPretextThenChars(paragraph, useMaskForChars)) {
				return
			}
		}
		const splittingOptions = { target: paragraph, by: splitBy }
		if (splitBy === 'chars') {
			splittingOptions.whitespace = true
		}
		try {
			const results = Splitting(splittingOptions)
			// Character-level animations need `.char` spans inside each
			// `.word`. The Pretext path already produces them; when
			// Pretext bails (e.g. the paragraph contains an inline
			// `<a class="laybutton">` that we don't want to flatten), we
			// land here with `splitBy='lines'`, which gives us words +
			// line indices but no chars. Add chars per-word manually —
			// this preserves the inline `<a>` wrapper (chars end up
			// inside the `<a>` → still a clickable link).
			if ((animationType === 'masked_characters' || animationType === 'fade_characters' || animationType === 'fade_and_slide_characters') && splitBy === 'lines') {
				paragraph.querySelectorAll('.word').forEach((word) => {
					splitWordIntoCharSpans(word)
				})
			}
			return results
		} catch (e) {
			// Splitting library can throw on certain paragraph structures —
			// we've seen `TypeError: can't access property "style", n is null`
			// when the paragraph contains an `<a class="laybutton...">` with
			// many `data-*` attributes. Fall back to a manual splitter for
			// char-level animations so the typewriter_fade / typewriter
			// types still work; for line-level / word-level we re-throw
			// because we don't have manual fallbacks for those yet.
			if (splitBy === 'chars') {
				console.warn('[Lay] Splitting library threw; using manual char splitter as fallback.', e)
				manuallySplitParagraphIntoChars(paragraph)
				return
			}
			throw e
		}
	})
}
let useLineMask = true

// Function to wrap lines in divs
const wrapLines = (target) => {
	const paragraphs = getTextAnimationBlockElements(target)

	// Check animation type to determine wrapping strategy
	const animationType = target.getAttribute('data-animation-type')
	const useLineMaskForType = useLineMask && shouldUseLineMaskForAnimationType(animationType)
	
	if (animationType === 'typewriter' || animationType === 'typewriter_fade') {
		// One .lay-text-line per paragraph so block divs do not stack as fake “lines”; clear stale pretext-split
		paragraphs.forEach((paragraph) => {
			consolidateTypewriterParagraphIntoSingleLine(paragraph)
		})
		return
	}

	// Original line wrapping logic for other animation types
	paragraphs.forEach((paragraph) => {
		if (paragraph.dataset.pretextSplit === 'true') {
			return
		}

		normalizeWhitespaceOnlyTextNodes(paragraph)
		const words = paragraph.querySelectorAll('.word')

		const lines = []
		words.forEach((word) => {
			const lineIndex = word.style.getPropertyValue('--line-index')
			if (!lines[lineIndex]) lines[lineIndex] = []
			lines[lineIndex].push(word)
		})

		// Wrap each line in a div with class 'lay-text-line'
		lines.forEach((lineWords, i) => {
			const wrapper = document.createElement('div')
			wrapper.className = 'lay-text-line'

			const lineElements = getLineElementsForWrap(paragraph, i)

			// Insert wrapper before first element of the line
			if (lineElements.length > 0) {
				lineElements[0].parentNode.insertBefore(wrapper, lineElements[0])
				// Move all elements into the wrapper
				lineElements.forEach((el) => wrapper.appendChild(el))
			}
		})

		// Remove br elements after wrapping is complete
		const brs = paragraph.querySelectorAll('br')
		brs.forEach((br) => br.remove())

		// Remove any whitespace spans that are between line divs
		const whitespaces = paragraph.querySelectorAll('.whitespace')
		whitespaces.forEach((whitespace) => {
			const prevElement = whitespace.previousElementSibling
			const nextElement = whitespace.nextElementSibling
			if (prevElement && nextElement && prevElement.classList.contains('lay-text-line') && nextElement.classList.contains('lay-text-line')) {
				whitespace.remove()
			}
		})

		// Wrap each line div in a line-mask div if useLineMask is true
		if (useLineMaskForType) {
			const lineDivs = paragraph.querySelectorAll('.lay-text-line')
			lineDivs.forEach((lineDiv) => {
				const maskWrapper = document.createElement('div')
				maskWrapper.className = 'lay-text-line-mask'
				applyLineMaskPaddingBottomStyle(maskWrapper, target)
				lineDiv.parentNode.insertBefore(maskWrapper, lineDiv)
				maskWrapper.appendChild(lineDiv)
			})
		}

		// Splitting drops the space between two words that share an inline
		// wrapper (e.g. <strong>Julia Wissert</strong> → two adjacent .word
		// spans, no whitespace node). Re-insert it. Runs AFTER line wrapping so
		// getLineElementsForWrap can't pull the freshly-inserted whitespace
		// back out of the <strong> while moving the wrapper into its line.
		ensureWhitespaceBetweenAdjacentWordElements(paragraph)
	})
}

/**
 * Unwraps `.char` / `.word` / `.whitespace` / `.lay-text-line-mask` /
 * `.lay-text-line` spans back to plain text. Walks from innermost to
 * outermost (chars first, then words, then lines) so each unwrap operates
 * on a node whose children are already plain text nodes. Cleans up
 * splitting-added paragraph attributes (`class="words"`, `--word-total`).
 *
 * Use case: after AJAX page-nav restores HTML from the lay-cache, that
 * HTML can contain the PREVIOUS page's JS-mutated split markup. Splitting
 * library throws when re-splitting already-split markup (`TypeError:
 * can't access property "style", n is null` deep in vendor code), so we
 * have to revert to the un-split form before letting prepareTexts run.
 */
const stripSplitMarkupIfPresent = (target) => {
	if (!target) return false
	if (
		!target.querySelector('.lay-text-line') &&
		!target.querySelector('.word') &&
		!target.querySelector('.char') &&
		!target.querySelector('.whitespace') &&
		!target.querySelector('.lay-text-line-mask')
	) {
		return false
	}
	const selectorsInOrder = ['.char', '.word', '.whitespace', '.lay-text-line-mask', '.lay-text-line']
	selectorsInOrder.forEach((sel) => {
		Array.from(target.querySelectorAll(sel)).forEach((node) => {
			const parent = node.parentNode
			if (!parent) return
			while (node.firstChild) {
				parent.insertBefore(node.firstChild, node)
			}
			parent.removeChild(node)
		})
	})
	target.querySelectorAll('p.words, h1.words, h2.words, h3.words, h4.words, h5.words, h6.words').forEach((p) => {
		p.classList.remove('words')
		if (p.style && p.style.removeProperty) p.style.removeProperty('--word-total')
		if (p.getAttribute('style') === '') p.removeAttribute('style')
		if (p.getAttribute('class') === '') p.removeAttribute('class')
	})
	// Adjacent text nodes left behind by the unwraps would confuse the
	// Splitting library (it iterates text nodes one at a time). Merge them.
	target.normalize()
	return true
}

/**
 * @param {Element[]=} explicitTargets — when content_ready scopes the call to
 *   one scope's targets (overlay or normal), pass that list directly. Without
 *   it we fall back to `getAllScopeTargets()` (the union across scopes), and
 *   the second content_ready (underlay) would re-touch already-rendered
 *   overlay targets — which is the bug class Phase 2 set out to prevent.
 *   Resize/font-load callers leave it undefined intentionally.
 */
const storeOriginalTexts = (explicitTargets) => {
	const targets = explicitTargets || getAllScopeTargets()

	targets.forEach((target) => {
		// Store original HTML if not already stored
		if (!jQuery(target).data('original-html')) {
			if (target.closest && target.closest('#lay-project-overlay')) {
				target.classList.remove('lay-text-animation-initialized')
				target.style.opacity = '0'
			}
			// AJAX nav can hand us a freshly-inserted DOM whose innerHTML
			// is the previous page's JS-mutated split state (lay-cache
			// snapshots the live DOM, not the server-rendered original).
			// Strip any existing split markup back to plain text before
			// stashing as "original" — otherwise prepareTexts would try
			// to re-split already-split markup and Splitting throws.
			stripSplitMarkupIfPresent(target)
			const originalHTML = target.innerHTML

			jQuery(target).data('original-html', originalHTML)
			jQuery(target).data('text-state', 'original')
		}
	})
}

/**
 * @param {boolean} triggerEvent
 * @param {Element[]=} explicitTargets — pass when scope-targeted (content_ready
 *   for overlay or normal). Resize/font-load callers leave it undefined, which
 *   means "iterate everything currently on the page".
 *
 * Phase 2 convention: NEW callers should pass explicitTargets — only resize
 * and font-load have a legitimate need for the union iteration. If you find
 * yourself omitting this argument, double-check that's actually correct.
 */
const prepareTexts = async (triggerEvent = true, explicitTargets) => {
	const targets = explicitTargets || getAllScopeTargets()
	let didMutateTargets = false
	let anyResizeMutation = false
	let anyFirstSplitMutation = false
	/** Targets that were resplit due to width change (not first split). Used when one batch mixes resize + first split (e.g. breakpoint shows a new duplicate block). */
	const resizeMutationTargets = new Set()
	/** Targets that were freshly first-split in this prepareTexts call. Listeners use this to filter their work to ONLY the targets whose DOM actually changed — without this, the direct-load flow (two content_ready scopes) would let the second prepareTexts trigger a replay of already-playing first-scope timelines. */
	const firstSplitMutationTargets = new Set()
	const stabilizationTasks = []

	taSplitLog('prepareTexts start', {
		triggerEvent,
		targetCount: targets.length,
	})
	taResizeTrace('prepareTexts start (grid)', {
		triggerEvent,
		targetCount: targets.length,
		innerWidth: typeof window !== 'undefined' ? window.innerWidth : null,
	})

	const fontsReadyBeforeSplit = await waitForTextAnimationFontsIfNeeded(targets)

	if (someVisibleTargetNeedsPretextLazyModule(targets)) {
		try {
			await ensureLayPretextTextAnimationLoaded(targets)
		} catch (e) {
			console.warn('[Lay] Pretext text animation chunk failed to load; using Splitting fallback.', e)
		}
	}

	targets.forEach((target) => {
		if (!jQuery(target).is(':visible')) {
			target.removeAttribute(PENDING_RESPLIT_ATTR)
			taResizeTraceTarget(target, 'prepareTexts branch', {
				branch: 'skip_not_visible',
				hadPending: target.hasAttribute(PENDING_RESPLIT_ATTR),
			})
			taSplitLogTarget(target, 'prepareTexts: skip (not :visible)')
			return
		}

		const pendingResizeResplit = target.hasAttribute(PENDING_RESPLIT_ATTR)

		if (pendingResizeResplit) {
			target.removeAttribute(PENDING_RESPLIT_ATTR)
			target.removeAttribute(PRETEXT_WIDTH_INSET_ATTR)
			const anim = target.getAttribute('data-animation-type')
			taResizeTraceTarget(target, 'prepareTexts branch', {
				branch: 'pending_resize_resplit',
				anim,
				textState: jQuery(target).data('text-state'),
			})
			taSplitLogTarget(target, 'prepareTexts branch: pendingResizeResplit', { anim })
			if (anim === 'fade_whole_text' || anim === 'fade_and_slide_whole_text') {
				const originalHTML = jQuery(target).data('original-html')
				if (originalHTML) {
					target.innerHTML = originalHTML
					target['🍌'] = null
				}
				jQuery(target).data('text-state', 'dirty')
				didMutateTargets = true
				anyResizeMutation = true
				resizeMutationTargets.add(target)
				applyPreRevealVisualStateToTarget(target)
			} else {
				atomicResplitLayTextAnimation(target)
				jQuery(target).data('text-state', 'dirty')
				didMutateTargets = true
				anyResizeMutation = true
				resizeMutationTargets.add(target)
				stabilizationTasks.push(stabilizePostLayoutIfNeeded(target))
			}
			return
		}

		if (jQuery(target).data('text-state') === 'original') {
			const anim = target.getAttribute('data-animation-type')
			taResizeTraceTarget(target, 'prepareTexts branch', {
				branch: 'first_split_original',
				anim,
			})
			taSplitLogTarget(target, 'prepareTexts branch: first split (text-state original)', { anim })
			if (anim === 'fade_whole_text' || anim === 'fade_and_slide_whole_text') {
				jQuery(target).data('text-state', 'dirty')
				firstSplitMutationTargets.add(target)
			} else {
				anyFirstSplitMutation = true
				splitText(target)
				wrapLines(target)
				jQuery(target).data('text-state', 'dirty')
				didMutateTargets = true
				firstSplitMutationTargets.add(target)
				taSplitLogTarget(target, 'prepareTexts: after wrapLines → preReveal')
				applyPreRevealVisualStateToTarget(target)
				stabilizationTasks.push(stabilizePostLayoutIfNeeded(target))
			}
		} else {
			taResizeTraceTarget(target, 'prepareTexts branch', {
				branch: 'skip_dirty_no_pending',
				textState: jQuery(target).data('text-state'),
			})
			taSplitLogTarget(target, 'prepareTexts: skip (already dirty / no pending)', {
				textState: jQuery(target).data('text-state'),
			})
		}
	})
	if (stabilizationTasks.length > 0) {
		taSplitLog('prepareTexts: await stabilization', { taskCount: stabilizationTasks.length })
		await Promise.all(stabilizationTasks)
	}
	const willEmit = !!(triggerEvent && didMutateTargets)
	const fromResizeOnly = !!(didMutateTargets && anyResizeMutation && !anyFirstSplitMutation)
	taSplitLog('prepareTexts end', {
		didMutateTargets,
		emitTextsAreSplit: willEmit,
		stabilizationRan: stabilizationTasks.length > 0,
		fromResizeOnly,
		resizeMutationTargetCount: resizeMutationTargets.size,
	})
	if (triggerEvent && didMutateTargets) {
		taSplitLog('prepareTexts: emit texts_are_split', {
			fromResize: fromResizeOnly,
			resizeMutationTargetCount: resizeMutationTargets.size,
		})
		taResizeTrace('prepareTexts emit texts_are_split', {
			fromResize: fromResizeOnly,
			anyFirstSplitMutation,
			anyResizeMutation,
			resizeMutationTargetCount: resizeMutationTargets.size,
		})
		window.laytheme.emit('texts_are_split', {
			fromResize: fromResizeOnly,
			resizeMutationTargets,
			firstSplitMutationTargets,
		})
	}
	if (!fontsReadyBeforeSplit) {
		scheduleResplitAfterLateFonts()
	}
}

const debouncedResizeEnd = debounce(() => {
	taResizeTrace('debouncedResizeEnd fired → prepareTexts (grid)', {
		innerWidth: typeof window !== 'undefined' ? window.innerWidth : null,
	})
	prepareTexts().catch(() => {})
}, 200)

let oldWindowWidth = window.innerWidth

// Initialize resize handling
const initResizeHandling = () => {
	jQuery(window).off('window_width_changed', markTextAnimationsPendingResplit)
	jQuery(window).off('window_width_changed', debouncedResizeEnd)

	jQuery(window).on('window_width_changed', markTextAnimationsPendingResplit)
	jQuery(window).on('window_width_changed', debouncedResizeEnd)

	if (window.laytheme && typeof window.laytheme.off === 'function' && typeof window.laytheme.on === 'function') {
		window.laytheme.off('sizechanged', onLayTextAnimSizeChanged)
		window.laytheme.on('sizechanged', onLayTextAnimSizeChanged)
	}
}
initResizeHandling()

export { prepareTexts, splitText, wrapLines, storeOriginalTexts }
